import { useState } from 'react';
import type { ImageObject } from '@ox/schema';
import { useEditorStore } from '../../store/editor-store';
import { BottomSheet } from './BottomSheet';
import {
  IconAspectLock,
  IconBringForward,
  IconCenterH,
  IconCenterV,
  IconLayers,
  IconPencil,
  IconPosition,
  IconSendBackward,
  IconTextSize,
  IconTrash,
} from '../Icons';

type ImageEditMode = 'menu' | 'position' | 'size' | 'layers';

export function ImageEditSheet({
  onClose,
  onReedit,
}: {
  onClose: () => void;
  onReedit?: () => void;
}) {
  const model = useEditorStore((s) => s.model);
  const selectedId = useEditorStore((s) => s.selectedId);
  const document = useEditorStore((s) => s.document);
  const activeView = useEditorStore((s) => s.activeView);
  const updateObject = useEditorStore((s) => s.updateObject);
  const centerH = useEditorStore((s) => s.centerH);
  const centerV = useEditorStore((s) => s.centerV);
  const bringForward = useEditorStore((s) => s.bringForward);
  const sendBackward = useEditorStore((s) => s.sendBackward);
  const moveSelectedToView = useEditorStore((s) => s.moveSelectedToView);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const lowResWarning = useEditorStore((s) => s.lowResWarning);
  const [mode, setMode] = useState<ImageEditMode>('menu');
  const [lockRatio, setLockRatio] = useState(true);

  const obj = document.views
    .find((v) => v.viewId === activeView)
    ?.objects.find((o) => o.id === selectedId && o.type === 'image') as ImageObject | undefined;

  const otherViews = (model?.views ?? []).filter((v) => v.id !== activeView);

  if (!obj) {
    return (
      <BottomSheet title="Imagen" onClose={onClose} docked>
        <p className="ox-hint">Selecciona o añade una imagen en el lienzo.</p>
      </BottomSheet>
    );
  }

  const backToMenu = () => setMode('menu');

  if (mode === 'position') {
    return (
      <BottomSheet title="Posición" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-icon-actions ox-icon-actions--compact">
          <button type="button" onClick={centerH}>
            <IconCenterH />
            <span>Centrar H</span>
          </button>
          <button type="button" onClick={centerV}>
            <IconCenterV />
            <span>Centrar V</span>
          </button>
        </div>
        {otherViews.length > 0 && (
          <>
            <p className="ox-section-label">Mover a otra vista</p>
            <div className="ox-icon-actions ox-icon-actions--compact">
              {otherViews.map((v) => (
                <button key={v.id} type="button" onClick={() => moveSelectedToView(v.id)}>
                  <IconSendBackward />
                  <span>{v.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </BottomSheet>
    );
  }

  if (mode === 'size') {
    const sizeMin = 10;
    const sizeMax = 400;
    const scale = Math.round(obj.width);
    const clamped = Math.min(sizeMax, Math.max(sizeMin, scale));
    const pct = ((clamped - sizeMin) / (sizeMax - sizeMin)) * 100;
    const setWidth = (w: number) => {
      const nextW = Math.min(sizeMax, Math.max(sizeMin, w));
      const nextH = lockRatio ? obj.height * (nextW / obj.width) : obj.height;
      updateObject(obj.id, { width: nextW, height: Math.max(sizeMin, nextH) });
    };
    return (
      <BottomSheet title="Tamaño" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-m-size-slider">
          <input
            type="range"
            min={sizeMin}
            max={sizeMax}
            step={1}
            value={clamped}
            style={{
              background: `linear-gradient(to right, #e07a6a ${pct}%, #e5e5e5 ${pct}%)`,
            }}
            onChange={(e) => setWidth(Number(e.target.value) || sizeMin)}
            aria-label="Ancho de la imagen"
          />
          <input
            type="number"
            min={sizeMin}
            max={sizeMax}
            value={Math.round(obj.width)}
            onChange={(e) => setWidth(Number(e.target.value) || sizeMin)}
            aria-label="Ancho en mm"
          />
        </div>
        <button
          type="button"
          className={`ox-lock-ratio ${lockRatio ? 'active' : ''}`}
          onClick={() => setLockRatio((v) => !v)}
          aria-pressed={lockRatio}
        >
          <IconAspectLock size={16} />{' '}
          {lockRatio ? 'Proporción bloqueada' : 'Proporción libre'}
        </button>
        <p className="ox-hint" style={{ marginTop: 8 }}>
          Alto: {Math.round(obj.height)} mm
        </p>
      </BottomSheet>
    );
  }

  if (mode === 'layers') {
    return (
      <BottomSheet title="Capas" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-icon-actions ox-icon-actions--compact">
          <button type="button" onClick={bringForward}>
            <IconBringForward />
            <span>Traer al frente</span>
          </button>
          <button type="button" onClick={sendBackward}>
            <IconSendBackward />
            <span>Enviar atrás</span>
          </button>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet title="Editar imagen" onDone={onClose} docked>
      {lowResWarning && (
        <div className="ox-warn">La imagen tiene baja resolución para imprimir bien.</div>
      )}
      <div className="ox-m-tool-grid">
        <button type="button" onClick={() => setMode('position')}>
          <IconPosition />
          <span>Posición</span>
        </button>
        <button type="button" onClick={() => setMode('size')}>
          <IconTextSize />
          <span>Tamaño</span>
        </button>
        <button type="button" onClick={() => setMode('layers')}>
          <IconLayers />
          <span>Capas</span>
        </button>
        {onReedit && !obj.galleryCode && (
          <button type="button" onClick={onReedit}>
            <IconPencil />
            <span>Editar imagen</span>
          </button>
        )}
        <button
          type="button"
          className="ox-m-tool-danger"
          onClick={() => {
            deleteSelected();
            onClose();
          }}
        >
          <IconTrash size={28} />
          <span>Borrar</span>
        </button>
      </div>
    </BottomSheet>
  );
}
